import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../styles/ThemeProvider';
import {
  FilterBar, DateRangePicker, ExportButton, KpiCard, ChartCard, DataTable,
  TrendLine, CategoryBar,
} from '../../components/analytics';
import { formatTRY, formatPct, formatCompact } from '../../utils/analytics/format';
import { exportPdf } from '../../utils/analytics/export';
import { getPresetRange, toIsoDate } from '../../utils/analytics/dateRange';
import { useAnalyticsQuery } from '../../hooks/useAnalyticsQuery';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fetchJson = async (url, signal) => {
  const res = await fetch(url, { signal, headers: authHeaders() });
  if (!res.ok) throw new Error(`Hata ${res.status}`);
  return res.json();
};

const initialRange = () => {
  const r = getPresetRange('thisMonth');
  return { preset: 'thisMonth', start: toIsoDate(r.start), end: toIsoDate(r.end) };
};

const riskColor = (risk, theme) => {
  if (risk === 'YÜKSEK') return theme.color.danger;
  if (risk === 'ORTA') return theme.color.warning;
  return theme.color.success;
};

export default function SatinalmaAnalizPage({ embedded = false }) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [range, setRange] = useState(initialRange);
  const containerRef = useRef(null);

  const params = useMemo(() => `baslangic=${range.start}&bitis=${range.end}`, [range]);

  const { data: konsantrasyon, loading: loadingK, error: errK } = useAnalyticsQuery(
    `konsantrasyon:${params}`,
    ({ signal }) => fetchJson(`${API}/api/analytics/satinalma/konsantrasyon?${params}`, signal),
    [params],
  );

  // TP-04: tedarikçi hacmi dönem filtresine uymalı → params'a bağlı (eski
  // supplier-performance ucu hem boş Siparis tablosundan besleniyor hem de dönem
  // almıyordu). tedarikci-performans = gerçek Fatura verisi + net baz + dönem.
  const { data: performans, loading: loadingP } = useAnalyticsQuery(
    `tedarikci-performans:${params}`,
    ({ signal }) => fetchJson(`${API}/api/raporlar/tedarikci-performans?${params}`, signal),
    [params],
  );

  const { data: aylik, loading: loadingA } = useAnalyticsQuery(
    'monthly-summary-12',
    ({ signal }) => fetchJson(`${API}/api/reports/monthly-summary?months=12`, signal),
    [],
  );

  const suppliers = useMemo(() => {
    // Backend /api/raporlar/tedarikci-performans response:
    //   { baz:'net', baslangic, bitis, siparis_verisi:false, tedarikciler:[
    //     { id, unvan, aktif, net_tutar, brut_tutar, kdv_belirsiz_fatura_sayisi,
    //       fatura_sayisi } ] }
    const raw = performans?.tedarikciler || [];
    return raw.map((r) => ({
      id: r.id,
      unvan: r.unvan || '—',
      hacim: Number(r.net_tutar ?? 0),     // TP-05: net (KDV hariç)
      brut: Number(r.brut_tutar ?? 0),
      fatura_sayisi: r.fatura_sayisi ?? null,
      kdv_belirsiz: r.kdv_belirsiz_fatura_sayisi ?? 0,
      aktif: r.aktif !== false,
    }));
  }, [performans]);

  const kdvBelirsizVar = useMemo(() => suppliers.some((s) => s.kdv_belirsiz > 0), [suppliers]);

  const trendData = useMemo(() => {
    const months = Array.isArray(aylik) ? aylik : (aylik?.months || aylik?.items || []);
    return months.map((m) => ({
      label: m.ay || m.month || m.label || '',
      harcama: Number(m.toplam_harcama || m.toplam || m.spending || 0),
    }));
  }, [aylik]);

  const top10 = useMemo(() =>
    [...suppliers].sort((a, b) => b.hacim - a.hacim).slice(0, 10).map(s => ({
      label: s.unvan.slice(0, 30),
      value: s.hacim,
    })),
    [suppliers]);

  const onPdf = () => {
    if (containerRef.current) exportPdf(containerRef.current, `satinalma-${range.start}_${range.end}`);
  };

  const riskBadgeLabel = konsantrasyon?.risk_level || '—';
  const riskClr = konsantrasyon ? riskColor(konsantrasyon.risk_level, theme) : theme.color.muted;

  const Wrapper = ({ children }) =>
    embedded ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.space.lg, fontFamily: theme.font.family }}>
        {children}
      </div>
    ) : (
      <div ref={containerRef} style={{
        padding: theme.space.lg, display: 'flex', flexDirection: 'column', gap: theme.space.lg,
        background: theme.color.surfaceAlt, minHeight: '100vh', fontFamily: theme.font.family,
      }}>{children}</div>
    );

  return (
    <Wrapper>
      {!embedded && (
        <header>
          <h1 style={{ margin: 0, color: theme.color.text, fontSize: theme.font.size.xxl, fontWeight: theme.font.weight.bold }}>
            Satınalma Analizi
          </h1>
          <div style={{ color: theme.color.textSoft, fontSize: theme.font.size.sm, marginTop: 4 }}>
            Tedarikçi hacim, konsantrasyon riski ve aylık harcama trendi
          </div>
        </header>
      )}

      <FilterBar sticky={!embedded}>
        <DateRangePicker value={range} onChange={setRange} />
        <div style={{ marginLeft: 'auto' }}>
          <ExportButton onExportPdf={onPdf} />
        </div>
      </FilterBar>

      {errK && <div style={{ color: theme.color.danger }}>{`Hata: ${errK}`}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: theme.space.base }}>
        <KpiCard title="Toplam Satınalma" value={konsantrasyon ? formatTRY(konsantrasyon.toplam_harcama) : undefined} loading={loadingK}
                 tooltip="Konsantrasyon (HHI) bazı: KDV dahil (brüt). Aşağıdaki tedarikçi listesi net (KDV hariç) gösterir." />
        <KpiCard title="Aktif Tedarikçi" value={konsantrasyon ? String(konsantrasyon.tedarikci_sayisi) : undefined} loading={loadingK} />
        <KpiCard title="Top-3 Share" value={konsantrasyon ? formatPct(konsantrasyon.top3_share_pct) : undefined} loading={loadingK} increaseIsBad />
        <div style={{
          background: theme.color.surface, border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radius.md, padding: theme.space.lg, boxShadow: theme.shadow.sm,
          display: 'flex', flexDirection: 'column', gap: theme.space.sm, minHeight: 120,
        }}
          title={konsantrasyon ? `HHI: ${Math.round(konsantrasyon.herfindahl_index)}` : ''}>
          <div style={{
            fontSize: theme.font.size.xs, fontWeight: theme.font.weight.semibold,
            color: theme.color.textSoft, textTransform: 'uppercase', letterSpacing: 0.6,
          }}>Konsantrasyon Riski</div>
          <div style={{
            fontSize: theme.font.size.xxl, fontWeight: theme.font.weight.bold, color: riskClr,
          }}>{riskBadgeLabel}</div>
          <div style={{ fontSize: theme.font.size.xs, color: theme.color.muted }}>
            {konsantrasyon ? `HHI: ${Math.round(konsantrasyon.herfindahl_index)}` : ''}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: theme.space.base }}>
        <ChartCard title="Aylık Harcama Trendi" subtitle="Son 12 ay" height={280}
                   loading={loadingA} empty={!loadingA && trendData.length === 0}>
          <TrendLine
            data={trendData}
            series={[{ key: 'harcama', label: 'Harcama' }]}
            yFormatter={formatCompact}
          />
        </ChartCard>

        <ChartCard title="Top 10 Tedarikçi (Net Hacim)" subtitle="KDV hariç" height={280}
                   loading={loadingP} empty={!loadingP && top10.length === 0}>
          <CategoryBar data={top10} formatter={formatCompact} />
        </ChartCard>
      </div>

      <ChartCard title="Tedarikçi Listesi" loading={loadingP} empty={!loadingP && suppliers.length === 0}>
        <DataTable
          rows={suppliers}
          columns={[
            { key: 'unvan', label: 'Unvan', render: (v, r) => r.aktif ? v : `${v} (pasif)` },
            { key: 'fatura_sayisi', label: 'Fatura', align: 'right', render: (v) => v == null ? '—' : v },
            { key: 'hacim', label: 'Hacim (Net, KDV hariç)', align: 'right',
              render: (v, r) => r.kdv_belirsiz > 0
                ? <span title={`${r.kdv_belirsiz} faturada KDV bazı belirsiz — net tahmini`}>{formatTRY(v)} ⚠</span>
                : formatTRY(v) },
          ]}
          onRowClick={(r) => navigate(`/analiz/tedarikci/${r.id}`)}
          initialSort={{ key: 'hacim', dir: 'desc' }}
        />
        <div style={{ marginTop: theme.space.sm, fontSize: theme.font.size.xs, color: theme.color.muted }}>
          Tutarlar <strong>net (KDV hariç)</strong> — mali raporlarla aynı baz. Konsantrasyon KPI'leri KDV dahildir.
          {' '}Sipariş verisi harici sistemde (Logo) tutulduğundan bu raporda yer almaz.
          {kdvBelirsizVar && ' ⚠ işaretli tedarikçilerde bazı faturaların KDV matrahı bilinmediğinden net tahminidir.'}
        </div>
      </ChartCard>
    </Wrapper>
  );
}
